import React, { ReactElement, useMemo } from 'react';

import { useConnection } from 'modules/auth/hooks/useConnection';
import { DECIMAL_PLACES_SHORT } from 'modules/common/const';
import { useHasDeprecatedLSToken } from 'modules/deprecatedLiquidStaking/hooks/useHasDeprecatedLSToken';
import { DeprecatedLiquidStakingRoutesConfig } from 'modules/deprecatedLiquidStaking/Routes';
import { KnownDialogs, useDialog } from 'modules/dialogs';
import { useGlobalTranslation } from 'modules/i18n';
import { StakeSuccessDialog } from 'modules/liquidStaking/components/StakeSuccessDialog/StakeSuccessDialog';
import { UnstakeSuccessDialog } from 'modules/liquidStaking/components/UnstakeSuccessDialog/UnstakeSuccessDialog';
import { useGetLiquidStakingAPY } from 'modules/liquidStaking/hooks/useGetLiquidStakingAPY';
import { useLiquidStakingPool } from 'modules/liquidStaking/hooks/useLiquidStakingPool';
import { useStakeToken } from 'modules/liquidStaking/hooks/useStakeToken';
import { LiquidStakingRoutesConfig } from 'modules/liquidStaking/Routes';
import { IModalSuccessState } from 'modules/liquidStaking/types';

import { LiquidStakingTable } from '../LiquidStakingTable';
import { ITableRowProps } from '../LiquidStakingTable/TableRow';
import { PendingLiquidStaking } from '../PendingLiquidStaking/PendingLiquidStaking';
import { StakingSection } from '../StakingSection';
import { LiquidStakingEmpty } from './LiquidStakingEmpty';
import { translation } from './translation';

export function LiquidStakingSection(): ReactElement {
  const { keys, t } = useGlobalTranslation(translation);

  const { poolAddress, isDeprecated } = useLiquidStakingPool();
  const { hasTokens } = useHasDeprecatedLSToken();
  const { balance, usdBalance, isBalanceLoading, address, name } =
    useStakeToken(poolAddress);

  const { isConnected } = useConnection();
  const { apy, isLoading: isAPYLoading } = useGetLiquidStakingAPY(poolAddress);

  const { isOpened: isTabStakeSuccessOpened } = useDialog<IModalSuccessState>(
    KnownDialogs.tabStakeSuccess,
  );

  const { isOpened: isTabUnstakeSuccessOpened } = useDialog<IModalSuccessState>(
    KnownDialogs.tabUnstakeSuccess,
  );

  const rows: ITableRowProps[] = useMemo(() => {
    return [
      {
        amount: balance,
        amountUsd: usdBalance,
        isLoading: isBalanceLoading || isAPYLoading,
        apy: apy.decimalPlaces(DECIMAL_PLACES_SHORT).toNumber(),
        tokenAddress: address,
        tokenName: name,
      },
    ];
  }, [address, apy, balance, isAPYLoading, isBalanceLoading, name, usdBalance]);

  const isEmpty = !isConnected || balance.isZero();

  const { title, titleLink } = useMemo(() => {
    if (isDeprecated) {
      return {
        title: t(keys.deprecatedLiquidStaking, { token: name }),
        titleLink: hasTokens
          ? DeprecatedLiquidStakingRoutesConfig.dashboard.generatePath()
          : '',
      };
    }

    return {
      title: t(keys.liquidStaking),
      titleLink: LiquidStakingRoutesConfig.dashboard.generatePath(),
    };
  }, [
    hasTokens,
    isDeprecated,
    keys.deprecatedLiquidStaking,
    keys.liquidStaking,
    name,
    t,
  ]);

  const isHideTokenTable = isDeprecated && !hasTokens;
  const table = isHideTokenTable ? null : <LiquidStakingTable rows={rows} />;

  return (
    <StakingSection
      isEmpty={isEmpty}
      isRed={isDeprecated}
      title={title}
      titleLink={titleLink}
    >
      {isEmpty ? <LiquidStakingEmpty /> : table}

      {isConnected && <PendingLiquidStaking />}

      {isTabStakeSuccessOpened && <StakeSuccessDialog />}

      {isTabUnstakeSuccessOpened && <UnstakeSuccessDialog />}
    </StakingSection>
  );
}
